import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { seoCollectionAPI } from "@/lib/api";
import PageSEO from "@/components/PageSEO";
import ReadingProgressBar from "@/components/ReadingProgressBar";
import GuideMap from "@/components/GuideMap";
import GoogleSourceCTA from "@/components/GoogleSourceCTA";
import {
  MapPin, Ticket, Sun, Bus, Train, Car, Loader2, ChevronRight,
  Clock, Globe, User, Star, ChevronDown, Home, Eye, Calendar, Lightbulb,
  Camera, Footprints, Smartphone, Mountain, Waves, Building2,
  Compass, Info, Heart, ArrowRight, Users, ShieldCheck, Map as MapIcon
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_THEMES = {
  temple: { accent: "#D88A2B", accentLight: "#FFF8EE", label: "Temple Guide", icon: Building2 },
  beach: { accent: "#1A7F8F", accentLight: "#EFF9FB", label: "Beach Guide", icon: Waves },
  fort: { accent: "#8B5E3C", accentLight: "#FBF5EF", label: "Fort Guide", icon: Mountain },
  general: { accent: "#2F5D3A", accentLight: "#F2F8F3", label: "Guide", icon: Compass },
};

const ICON_MAP = {
  location: MapPin, deity: Star, timings: Clock, fee: Ticket,
  season: Sun, dress: ShieldCheck, distance: Car, bus: Bus,
  train: Train, difficulty: Mountain, elevation: Mountain,
  info: Info, camera: Camera, footwear: Footprints,
  phone: Smartphone, ideal: Heart, water: Waves, built: Building2, users: Users,
};

/* ── Quick Facts Strip ── */
const QuickFacts = ({ items, theme }) => {
  if (!items?.length) return null;
  return (
    <div className="bg-white border-b border-gray-100" data-testid="quick-facts">
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-4">
        <div className="flex flex-wrap items-center gap-6 md:gap-10">
          {items.map((item, i) => {
            const Icon = ICON_MAP[item.icon] || Info;
            return (
              <div key={i} className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${theme.accent}12` }}>
                  <Icon className="w-4 h-4" style={{ color: theme.accent }} />
                </div>
                <div>
                  <p className="text-[11px] text-gray-400 leading-none">{item.label}</p>
                  <p className="text-sm font-semibold text-gray-800">{item.value}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/* ── Sticky Sidebar ── */
const Sidebar = ({ sections, places, hasFaqs, hasTips, theme, homestays }) => {
  const [activeId, setActiveId] = useState("overview");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const vis = entries.filter(e => e.isIntersecting);
        if (vis.length) setActiveId(vis[0].target.id);
      },
      { rootMargin: "-100px 0px -50% 0px", threshold: 0.1 }
    );
    document.querySelectorAll("[data-toc]").forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [sections, places]);

  const items = [
    { id: "overview", label: "Overview" },
    { id: "map-section", label: "Map" },
    ...(sections || []).map(s => ({
      id: `section-${s.slug || s.title.toLowerCase().replace(/\s+/g, "-")}`,
      label: s.title
    })),
    ...(places?.length > 0 ? [{ id: "places-heading", label: `Places (${places.length})`, children: places.map((p, i) => ({ id: `place-${i}`, label: `${i + 1}. ${p.name}` })) }] : []),
    ...(hasFaqs ? [{ id: "faqs", label: "FAQs" }] : []),
    ...(hasTips ? [{ id: "travel-tips", label: "Travel Tips" }] : []),
  ];

  return (
    <aside className="hidden lg:block w-[240px] flex-shrink-0" data-testid="guide-sidebar">
      <div className="sticky top-20 space-y-5">
        {/* TOC */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <h4 className="text-[11px] font-bold uppercase tracking-[0.15em] text-gray-400 mb-4">On This Page</h4>
          <nav className="space-y-0.5">
            {items.map((item) => (
              <div key={item.id}>
                <a href={`#${item.id}`}
                  className={`block text-[13px] py-1.5 px-3 rounded-lg transition-all leading-snug ${
                    activeId === item.id
                      ? "font-semibold text-white shadow-sm"
                      : "text-gray-500 hover:text-gray-800 hover:bg-gray-50"
                  }`}
                  style={activeId === item.id ? { backgroundColor: theme.accent } : {}}
                >
                  {item.label}
                </a>
                {item.children && (
                  <div className="ml-3 mt-0.5 space-y-0">
                    {item.children.map(child => (
                      <a key={child.id} href={`#${child.id}`}
                        className={`block text-[12px] py-1 px-3 rounded-md transition-all leading-snug ${
                          activeId === child.id
                            ? "font-semibold text-white shadow-sm"
                            : "text-gray-400 hover:text-gray-700 hover:bg-gray-50"
                        }`}
                        style={activeId === child.id ? { backgroundColor: theme.accent } : {}}
                      >
                        {child.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>
        </div>

        {/* Explore Nearby Homestays Card */}
        {homestays?.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-testid="sidebar-homestays">
            <div className="h-28 relative overflow-hidden">
              {homestays[0]?.images?.[0] ? (
                <img src={homestays[0].images[0]} alt="Stay nearby" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-green-50 to-teal-50 flex items-center justify-center">
                  <Home className="w-10 h-10 text-green-200" />
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              <p className="absolute bottom-3 left-4 text-white text-sm font-semibold">Explore Nearby Homestays</p>
            </div>
            <div className="p-4 space-y-3">
              {homestays.slice(0, 3).map((h) => (
                <Link key={h.id} to={`/homestay/${h.slug || h.id}`} className="flex items-center gap-2.5 group">
                  {h.images?.[0] ? (
                    <img src={h.images[0]} alt={h.name} className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0"><Home className="w-4 h-4 text-gray-300" /></div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-gray-700 truncate group-hover:text-primary transition-colors">{h.name}</p>
                    {h.average_rating > 0 && (
                      <span className="text-[11px] text-gray-400 flex items-center gap-0.5"><Star className="w-2.5 h-2.5 fill-amber-400 text-amber-400" />{h.average_rating?.toFixed(1)}</span>
                    )}
                  </div>
                </Link>
              ))}
              <Link to="/listings" className="flex items-center gap-1 text-xs font-semibold pt-1 transition-colors" style={{ color: theme.accent }}>
                Explore Stays <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
};

/* ── Map Section ── */
const MapSection = ({ mapUrl, locationName, title, theme, places }) => {
  const hasCoords = places?.some(p => p.latitude && p.longitude);
  if (!mapUrl && !locationName && !hasCoords) return null;

  return (
    <div id="map-section" data-toc className="mb-10 scroll-mt-24" data-testid="guide-map">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${theme.accent}12` }}>
          <MapIcon className="w-4 h-4" style={{ color: theme.accent }} />
        </div>
        <h2 className="text-xl md:text-2xl font-bold text-gray-900">Explore on Map</h2>
        {places?.filter(p => p.latitude && p.longitude).length > 0 && (
          <span className="text-xs text-gray-400 ml-auto">{places.filter(p => p.latitude && p.longitude).length} locations pinned</span>
        )}
      </div>
      <GuideMap places={places} locationName={locationName} theme={theme} />
    </div>
  );
};

/* ── Section Block ── */
const SectionBlock = ({ section, theme }) => {
  const sid = `section-${section.slug || section.title.toLowerCase().replace(/\s+/g, "-")}`;
  return (
    <div id={sid} data-toc className="mb-10 scroll-mt-24">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-1.5 h-7 rounded-full" style={{ backgroundColor: theme.accent }} />
        <h2 className="text-xl md:text-2xl font-bold text-gray-900">{section.title}</h2>
      </div>
      {section.image && (
        <div className="rounded-2xl overflow-hidden mb-5 aspect-[16/7]">
          <img src={section.image} alt={section.title} className="w-full h-full object-cover" loading="lazy" />
        </div>
      )}
      {section.content && (
        <div className="prose prose-gray max-w-none text-gray-600 leading-relaxed prose-headings:text-gray-800 prose-h3:text-lg prose-p:mb-3 prose-img:rounded-xl"
          dangerouslySetInnerHTML={{ __html: section.content }} />
      )}
    </div>
  );
};

/* ── Place Card ── */
const PlaceCard = ({ place, index, theme }) => {
  const mainImg = place.images?.[0] || place.image;
  const imgSrc = mainImg?.startsWith("/") ? `${BACKEND_URL}${mainImg}` : mainImg;

  return (
    <div id={`place-${index}`} data-toc className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 mb-6 scroll-mt-24" data-testid={`guide-place-${place.id}`}>
      {/* Full-width image */}
      <div className="relative aspect-[16/7] bg-gray-100 overflow-hidden">
        {imgSrc ? (
          <img src={imgSrc} alt={place.name} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
            <Globe className="w-16 h-16 text-gray-200" />
          </div>
        )}
        <span className="absolute top-4 left-4 w-10 h-10 rounded-xl flex items-center justify-center text-base font-bold text-white shadow-lg" style={{ backgroundColor: theme.accent }}>
          {index + 1}
        </span>
        {place.images?.length > 1 && (
          <div className="absolute bottom-3 right-3 flex gap-1.5">
            {place.images.slice(1, 4).map((img, i) => (
              <div key={i} className="w-14 h-14 rounded-lg overflow-hidden border-2 border-white/80 shadow">
                <img src={img.startsWith("/") ? `${BACKEND_URL}${img}` : img} alt="" className="w-full h-full object-cover" loading="lazy" />
              </div>
            ))}
            {place.images.length > 4 && (
              <div className="w-14 h-14 rounded-lg bg-black/50 border-2 border-white/80 flex items-center justify-center">
                <span className="text-white text-xs font-semibold">+{place.images.length - 4}</span>
              </div>
            )}
          </div>
        )}
      </div>
      {/* Content */}
      <div className="p-5 md:p-6">
        <div className="flex items-start justify-between gap-3 mb-2">
          <h3 className="text-lg md:text-xl font-bold text-gray-900">{place.name}</h3>
          <span className="px-2.5 py-1 bg-gray-50 text-[11px] font-semibold uppercase tracking-wider rounded-full text-gray-500 flex-shrink-0">{place.category}</span>
        </div>
        {place.location_name && (
          <p className="text-sm text-gray-400 mb-3 flex items-center gap-1">
            <MapPin className="w-3.5 h-3.5" style={{ color: theme.accent }} />
            {place.sub_location_name ? `${place.sub_location_name}, ` : ""}{place.location_name}
          </p>
        )}
        {place.description && (
          <div className="text-sm text-gray-600 mb-4 leading-relaxed" dangerouslySetInnerHTML={{ __html: place.description }} />
        )}
        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-4">
          {place.best_season && (
            <span className="text-xs font-medium px-3 py-1.5 rounded-full flex items-center gap-1.5" style={{ backgroundColor: `${theme.accent}10`, color: theme.accent }}>
              <Sun className="w-3 h-3" />{place.best_season}
            </span>
          )}
          {place.entry_fee && (
            <span className="text-xs font-medium bg-amber-50 text-amber-700 px-3 py-1.5 rounded-full flex items-center gap-1.5">
              <Ticket className="w-3 h-3" />{place.entry_fee}
            </span>
          )}
          {place.nearest_bus_stand && (
            <span className="text-xs font-medium bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full flex items-center gap-1.5">
              <Bus className="w-3 h-3" />Bus: {place.nearest_bus_stand}
            </span>
          )}
          {place.nearest_railway_station && (
            <span className="text-xs font-medium bg-purple-50 text-purple-700 px-3 py-1.5 rounded-full flex items-center gap-1.5">
              <Train className="w-3 h-3" />Rail: {place.nearest_railway_station}
            </span>
          )}
        </div>
        {/* Things to Do */}
        {place.things_to_do?.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Things to Do</p>
            <div className="flex flex-wrap gap-1.5">
              {place.things_to_do.slice(0, 5).map((t, i) => (
                <span key={i} className="text-xs bg-gray-50 text-gray-600 px-2.5 py-1 rounded-full">{t}</span>
              ))}
            </div>
          </div>
        )}
        <Link to={`/place/${place.slug || place.id}`} className="inline-flex items-center gap-1.5 text-sm font-semibold transition-colors group" style={{ color: theme.accent }} data-testid={`explore-${place.id}`}>
          View Full Details <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
    </div>
  );
};

/* ── FAQ Accordion ── */
const FAQSection = ({ places, theme }) => {
  const [openIdx, setOpenIdx] = useState(null);
  const allFaqs = [];
  (places || []).forEach(p => {
    (p.faqs || []).forEach(f => { if (f.question && f.answer) allFaqs.push({ ...f, placeName: p.name }); });
  });
  if (allFaqs.length === 0) return null;

  return (
    <div id="faqs" data-toc className="mb-10 scroll-mt-24" data-testid="guide-faqs">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-1.5 h-7 rounded-full bg-amber-500" />
        <h2 className="text-xl md:text-2xl font-bold text-gray-900">Frequently Asked Questions</h2>
      </div>
      <div className="space-y-2">
        {allFaqs.slice(0, 12).map((faq, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <button onClick={() => setOpenIdx(openIdx === i ? null : i)}
              className="w-full flex items-center justify-between p-4 md:p-5 text-left hover:bg-gray-50/50 transition-colors" data-testid={`faq-${i}`}>
              <span className="text-sm font-semibold text-gray-800 pr-4">{faq.question}</span>
              <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform duration-200 ${openIdx === i ? "rotate-180" : ""}`} />
            </button>
            <div className={`overflow-hidden transition-all duration-200 ${openIdx === i ? "max-h-[500px]" : "max-h-0"}`}>
              <div className="px-5 pb-5 text-sm text-gray-600 leading-relaxed border-t border-gray-50 pt-3">
                {faq.answer}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ── Travel Tips ── */
const TravelTips = ({ tips, theme }) => {
  if (!tips?.length) return null;
  return (
    <div id="travel-tips" data-toc className="mb-10 scroll-mt-24" data-testid="guide-travel-tips">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-1.5 h-7 rounded-full" style={{ backgroundColor: theme.accent }} />
        <h2 className="text-xl md:text-2xl font-bold text-gray-900">Travel Tips</h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {tips.map((tip, i) => (
          <div key={i} className="flex items-start gap-3 bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${theme.accent}12` }}>
              <Lightbulb className="w-3.5 h-3.5" style={{ color: theme.accent }} />
            </div>
            <p className="text-sm text-gray-600 leading-relaxed">{tip}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ── Related Guides ── */
const RelatedGuides = ({ guides, theme }) => {
  if (!guides?.length) return null;
  return (
    <div className="mb-10" data-testid="related-guides">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-1.5 h-7 rounded-full" style={{ backgroundColor: theme.accent }} />
        <h2 className="text-xl md:text-2xl font-bold text-gray-900">Related Guides</h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {guides.map((g) => (
          <Link key={g.id} to={`/guide/${g.slug}`} className="bg-white rounded-xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition-all group">
            <div className="aspect-[16/9] bg-gray-100 overflow-hidden">
              {g.featured_image ? (
                <img src={g.featured_image} alt={g.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
              ) : (
                <div className="w-full h-full flex items-center justify-center"><Globe className="w-10 h-10 text-gray-200" /></div>
              )}
            </div>
            <div className="p-4">
              <p className="text-sm font-semibold text-gray-800 line-clamp-2 group-hover:text-primary transition-colors">{g.title}</p>
              <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{g.views || 0} views</span>
                {g.location_name && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{g.location_name}</span>}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

/* ── Nearby Homestays Section (mobile) ── */
const NearbyHomestaysMobile = ({ homestays, theme }) => {
  if (!homestays?.length) return null;
  return (
    <div className="lg:hidden mb-10" data-testid="nearby-homestays-mobile">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-1.5 h-7 rounded-full" style={{ backgroundColor: theme.accent }} />
        <h2 className="text-xl font-bold text-gray-900">Nearby Homestays</h2>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-2 px-2" style={{ scrollbarWidth: "thin" }}>
        {homestays.slice(0, 6).map((h) => (
          <Link key={h.id} to={`/homestay/${h.slug || h.id}`} className="flex-shrink-0 w-[200px] bg-white rounded-xl overflow-hidden border border-gray-100 shadow-sm group">
            {h.images?.[0] ? (
              <div className="aspect-[4/3] overflow-hidden"><img src={h.images[0]} alt={h.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" /></div>
            ) : (
              <div className="aspect-[4/3] bg-gray-100 flex items-center justify-center"><Home className="w-8 h-8 text-gray-200" /></div>
            )}
            <div className="p-3">
              <p className="text-xs font-semibold text-gray-800 truncate">{h.name}</p>
              {h.average_rating > 0 && (
                <span className="text-[11px] text-gray-400 flex items-center gap-0.5 mt-1"><Star className="w-2.5 h-2.5 fill-amber-400 text-amber-400" />{h.average_rating?.toFixed(1)}</span>
              )}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

/* ═══════════════════ MAIN PAGE ═══════════════════ */
const SeoCollectionPage = () => {
  const { slug } = useParams();
  const [collection, setCollection] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try { const r = await seoCollectionAPI.getPublicBySlug(slug); setCollection(r.data); }
      catch { setCollection(null); }
      finally { setLoading(false); }
    };
    fetch();
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen bg-[#FAFAF8]"><Navbar />
      <div className="flex items-center justify-center py-40"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
    <Footer /></div>
  );

  if (!collection) return (
    <div className="min-h-screen bg-[#FAFAF8]"><Navbar />
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <h1 className="text-3xl font-bold text-gray-800 mb-4">Guide Not Found</h1>
        <Link to="/blog" className="text-primary hover:underline">Browse all guides</Link>
      </div>
    <Footer /></div>
  );

  const places = collection.places || [];
  const cat = collection.category || "general";
  const theme = CATEGORY_THEMES[cat] || CATEGORY_THEMES.general;
  const hasFaqs = places.some(p => p.faqs?.length > 0);
  const hasTips = collection.travel_tips?.length > 0;
  const fmtDate = (d) => new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="seo-collection-page">
      <ReadingProgressBar />
      <Navbar />
      <PageSEO
        title={collection.meta_title || collection.title}
        description={collection.meta_description}
        path={`/guide/${collection.slug}`}
        image={collection.featured_image}
        canonicalUrl={collection.canonical_url}
      />

      {/* ── HERO ── */}
      <section className="relative" data-testid="guide-hero">
        <div className="relative h-[340px] md:h-[440px] overflow-hidden">
          {collection.featured_image ? (
            <img src={collection.featured_image} alt={collection.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full" style={{ background: `linear-gradient(135deg, ${theme.accent}22 0%, ${theme.accent}08 100%)` }} />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/10" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
            <div className="max-w-6xl mx-auto">
              {/* Breadcrumbs */}
              <nav className="flex items-center gap-1.5 text-sm text-white/60 mb-4 flex-wrap" data-testid="guide-breadcrumbs">
                <Link to="/" className="hover:text-white transition-colors">Home</Link>
                <ChevronRight className="w-3 h-3" />
                <Link to="/blog" className="hover:text-white transition-colors">Guides</Link>
                <ChevronRight className="w-3 h-3" />
                <span className="text-white/90 truncate max-w-[300px]">{collection.title}</span>
              </nav>
              {/* Badge */}
              <span className="inline-block px-3 py-1 text-[11px] font-bold uppercase tracking-[0.12em] rounded-md text-white mb-3" style={{ backgroundColor: theme.accent }}>
                {theme.label}
              </span>
              {/* Title */}
              <h1 className="text-3xl md:text-4xl lg:text-[2.8rem] font-bold text-white leading-tight mb-3 max-w-3xl" data-testid="collection-title">
                {collection.title}
              </h1>
              {collection.meta_description && (
                <p className="text-base text-white/75 max-w-2xl leading-relaxed mb-4">{collection.meta_description}</p>
              )}
              {/* Meta */}
              <div className="flex flex-wrap items-center gap-5 text-sm text-white/50">
                <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />Updated {fmtDate(collection.updated_at)}</span>
                {collection._author && <span className="flex items-center gap-1.5"><User className="w-3.5 h-3.5" />By {collection._author.name}</span>}
                <span className="flex items-center gap-1.5"><Globe className="w-3.5 h-3.5" />{places.length} places</span>
                <span className="flex items-center gap-1.5"><Eye className="w-3.5 h-3.5" />{collection.views} views</span>
                <GoogleSourceCTA />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Facts */}
      <QuickFacts items={collection.quick_info} theme={theme} />

      {/* ── CONTENT AREA ── */}
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        <div className="flex gap-8">
          {/* Sidebar */}
          <Sidebar
            sections={collection.sections} places={places}
            hasFaqs={hasFaqs} hasTips={hasTips} theme={theme}
            homestays={collection.nearby_homestays}
          />

          {/* Main */}
          <div className="flex-1 min-w-0">
            {/* Overview */}
            {collection.description && (
              <div id="overview" data-toc className="mb-10 scroll-mt-24" data-testid="guide-overview">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-1.5 h-7 rounded-full" style={{ backgroundColor: theme.accent }} />
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900">Overview</h2>
                </div>
                <div className="prose prose-gray max-w-none text-gray-600 leading-relaxed prose-headings:text-gray-800 prose-h3:text-lg prose-p:mb-3 prose-img:rounded-xl"
                  dangerouslySetInnerHTML={{ __html: collection.description }} />
              </div>
            )}

            {/* Map */}
            <MapSection mapUrl={collection.map_embed_url} locationName={collection.location_name} title={collection.title} theme={theme} places={places} />

            {/* Custom Sections */}
            {(collection.sections || []).sort((a, b) => (a.order || 0) - (b.order || 0)).map((s, i) => (
              <SectionBlock key={i} section={s} theme={theme} />
            ))}

            {/* Places */}
            {places.length > 0 && (
              <div className="mb-10">
                <div id="places-heading" data-toc className="flex items-center gap-3 mb-6 scroll-mt-24">
                  <div className="w-1.5 h-7 rounded-full" style={{ backgroundColor: theme.accent }} />
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900">Places in this Guide</h2>
                  <span className="ml-auto text-xs font-medium bg-gray-100 text-gray-500 px-2.5 py-1 rounded-full">{places.length} places</span>
                </div>
                {places.map((p, i) => <PlaceCard key={p.id} place={p} index={i} theme={theme} />)}
              </div>
            )}

            {/* Nearby Homestays Mobile */}
            <NearbyHomestaysMobile homestays={collection.nearby_homestays} theme={theme} />

            {/* Travel Tips */}
            <TravelTips tips={collection.travel_tips} theme={theme} />

            {/* FAQs */}
            <FAQSection places={places} theme={theme} />

            {/* Related Guides */}
            <RelatedGuides guides={collection.related_guides} theme={theme} />

            {/* Author Box */}
            {collection._author && (
              <div className="mt-8 pt-8 border-t border-gray-200" data-testid="guide-author-box">
                <div className="flex items-start gap-5 bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                  {collection._author.photo ? (
                    <img src={collection._author.photo} alt={collection._author.name} className="w-16 h-16 rounded-full object-cover ring-2 ring-gray-100 flex-shrink-0" />
                  ) : (
                    <div className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${theme.accent}15` }}>
                      <span className="text-xl font-semibold" style={{ color: theme.accent }}>{collection._author.name?.charAt(0)?.toUpperCase()}</span>
                    </div>
                  )}
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-gray-400 mb-1">Written by</p>
                    <h4 className="text-lg font-bold text-gray-900">{collection._author.name}</h4>
                    {collection._author.bio && <p className="text-sm text-gray-500 mt-1 leading-relaxed">{collection._author.bio}</p>}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SeoCollectionPage;
